import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getMsUntilExpiry, refreshAuthToken } from '../../../services/authService';
import { isDictationActive } from '../../../utils/dictationActivity';
import { SessionExpiryModal } from './index';

/**
 * Sound played once each time the expiry modal appears, so a radiologist who
 * is not looking at the screen still notices the session is about to lapse.
 * Lazy singleton — created on first play() to avoid loading the asset on pages
 * where the warning never fires (e.g. /login). Autoplay is allowed here because
 * the modal only opens after the user has interacted with the app (login).
 */
let alarmAudio: HTMLAudioElement | null = null;
function playAlarm() {
  try {
    if (!alarmAudio) {
      alarmAudio = new Audio('S8.wav');
      alarmAudio.preload = 'auto';
    }
    alarmAudio.currentTime = 0;
    void alarmAudio.play().catch(() => {
      /* Swallow rejection: blocked autoplay (no prior user gesture) or the
         user hasn't allowed sound — the visible modal still warns them. */
    });
  } catch {
    /* Audio construction can throw on very old browsers — non-fatal. */
  }
}

/** Show the warning this long before the token's `exp`. */
const WARN_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
/**
 * Slack over WARN_BEFORE_MS used to detect a background refresh: if, while the
 * modal is open, the token suddenly has more than 5 min of life left, it was
 * refreshed by another request (or another tab) and the warning is stale.
 */
const REFRESH_DETECT_SLACK_MS = 5_000;

/**
 * A user who interacted within this window is considered "present": at the
 * warning point we refresh their token silently instead of showing the modal,
 * so active work is never interrupted. Only genuinely idle users see the modal.
 */
const ACTIVITY_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Watches the auth token's expiry. Five minutes before it lapses:
 *  - if the radiologist has interacted recently (see ACTIVITY_WINDOW_MS), the
 *    token is refreshed *silently* so active work is never interrupted;
 *  - if they are idle, a blocking countdown modal appears. They can extend the
 *    session ("Seguir conectado" → token refresh) or leave; if the countdown
 *    reaches zero with no renewal they are logged out, so nobody keeps working
 *    into a dead session only to lose their report to a surprise 401.
 *
 * Mounted once at the app root. Reschedules itself when:
 *  - authentication state flips (login / logout),
 *  - the token changes in another tab (`storage` event),
 *  - the tab regains focus (background timers get throttled, so we re-check).
 */
export function SessionExpiryManager() {
  const { isAuthenticated, logout } = useAuth();

  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [renewing, setRenewing] = useState(false);

  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Mirrors `visible` so timer callbacks can tell a fresh open from a re-open. */
  const visibleRef = useRef(false);
  /** Epoch-ms of the last user interaction; drives silent-refresh-vs-warn. */
  const lastActivityRef = useRef(Date.now());
  /** Guards against overlapping silent refreshes. */
  const silentRefreshingRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current !== null) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (tickTimerRef.current !== null) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const handleExpired = useCallback(() => {
    clearTimers();
    setVisible(false);
    setRenewing(false);
    logout();
  }, [clearTimers, logout]);

  // Forward declaration so showWarning and reschedule can call each other.
  const scheduleRef = useRef<() => void>(() => {});

  const showWarning = useCallback((initialMsLeft: number) => {
    clearTimers();
    // Already expired (e.g. the token vanished after a failed silent refresh):
    // don't render a dead 00:00 modal waiting for a tick — log out now so the
    // user is redirected to /login instead of the countdown freezing at zero.
    if (initialMsLeft <= 0) {
      handleExpired();
      return;
    }
    setSecondsLeft(Math.max(0, Math.ceil(initialMsLeft / 1000)));
    // Audible cue so the radiologist notices even when looking away from the
    // screen. Only on a fresh open — a tab re-focus reschedules and would
    // otherwise replay the alarm on every focus while the modal is already up.
    // Harmless if autoplay is blocked — the modal still shows.
    if (!visibleRef.current) playAlarm();
    setVisible(true);

    
    tickTimerRef.current = setInterval(() => {
      const msLeft = getMsUntilExpiry();
      // Token vanished (logout elsewhere / storage cleared) → end the session.
      if (msLeft === null) {
        handleExpired();
        return;
      }
      // Token was refreshed in the background (by a request or another tab):
      // the warning is stale — hide it and reschedule for the new expiry.
      if (msLeft > WARN_BEFORE_MS + REFRESH_DETECT_SLACK_MS) {
        clearTimers();
        setVisible(false);
        scheduleRef.current();
        return;
      }
      // Countdown finished with no renewal → log out.
      if (msLeft <= 0) {
        handleExpired();
        return;
      }
      setSecondsLeft(Math.ceil(msLeft / 1000));
    }, 1000);
  }, [clearTimers, handleExpired]);

  // At the warning point, keep a present-and-working radiologist logged in
  // *silently* instead of interrupting them: the modal only makes sense for an
  // idle user who won't otherwise notice the session lapsing. Falls back to the
  // modal if the silent refresh fails so they still get a chance to react.
  const warnOrRefresh = useCallback((msLeft: number) => {
    // Voice dictation produces no mouse/keyboard events, so treat an in-progress
    // dictation as activity too — otherwise a radiologist speaking (but not
    // touching the mouse) would be interrupted by the modal mid-dictation.
    const active =
      isDictationActive() || Date.now() - lastActivityRef.current < ACTIVITY_WINDOW_MS;
    if (!active) {
      showWarning(msLeft);
      return;
    }
    if (silentRefreshingRef.current) return;
    silentRefreshingRef.current = true;
    void refreshAuthToken()
      .then(() => {
        // Hide the modal in case a race (focus/visibility re-check) opened it
        // while this silent refresh was in flight, then reschedule for the new exp.
        silentRefreshingRef.current = false;
        setVisible(false);
        scheduleRef.current();
      })
      .catch(() => {
        // Silent refresh failed (network, or grace period truly exceeded):
        // fall back to the modal so the user can react instead of a surprise 401.
        silentRefreshingRef.current = false;
        showWarning(getMsUntilExpiry() ?? 0);
      });
  }, [showWarning]);

  const scheduleWarning = useCallback(() => {
    clearTimers();
    const msLeft = getMsUntilExpiry();
    // No readable expiry → can't warn proactively (server still enforces auth).
    if (msLeft === null) {
      // But if the modal is already up, the token was cleared mid-warning
      // (typically a failed refresh removed it) → the session is dead. Log out
      // instead of bailing here, which would kill the redirect timer and leave
      // the countdown frozen at 00:00 with no way back to /login.
      if (visibleRef.current) handleExpired();
      return;
    }
    if (msLeft <= WARN_BEFORE_MS) {
      warnOrRefresh(msLeft);
    } else {
      warnTimerRef.current = setTimeout(() => {
        warnOrRefresh(getMsUntilExpiry() ?? 0);
      }, msLeft - WARN_BEFORE_MS);
    }
  }, [clearTimers, warnOrRefresh, handleExpired]);

  // Keep the ref pointing at the latest scheduleWarning for cross-callbacks.
  useEffect(() => {
    scheduleRef.current = scheduleWarning;
  }, [scheduleWarning]);

  // Mirror `visible` into a ref so timer callbacks (which run outside render)
  // can distinguish a fresh open from a re-open without depending on state.
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const handleRenew = useCallback(() => {
    // Freeze the countdown while the refresh round-trips. Otherwise the ticking
    // timer can hit 0 mid-request and log the user out — racing (and defeating)
    // their own "stay connected" click. This is trivial to trigger on a
    // short-lived token but can also bite in production if the refresh is slow
    // and little time is left.
    clearTimers();
    setRenewing(true);
    void refreshAuthToken()
      .then(() => {
        setVisible(false);
        setRenewing(false);
        scheduleWarning();
      })
      .catch(() => {
        // Refresh failed (beyond the grace period) → session is truly dead.
        handleExpired();
      });
  }, [clearTimers, handleExpired, scheduleWarning]);

  // (Re)schedule whenever auth state changes; tear down when logged out.
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      // Reset the guard so a refresh that was in flight when the session ended
      // can't wedge the next login (warnOrRefresh would return early forever).
      silentRefreshingRef.current = false;
      setVisible(false);
      return;
    }
    scheduleWarning();
    return clearTimers;
  }, [isAuthenticated, scheduleWarning, clearTimers]);

  // Cross-tab token changes + focus re-check (background timers get throttled).
  useEffect(() => {
    if (!isAuthenticated) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'authToken') scheduleWarning();
    };
    const onFocus = () => scheduleWarning();
    // Switching to another tab in the SAME window throttles timers but fires no
    // window `focus` on return — only `visibilitychange`. Re-check on both so a
    // token that lapsed while the tab was hidden is caught the moment it shows.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleWarning();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, scheduleWarning]);

  // Track user interaction so the warning point can silently extend the session
  // for someone actively working instead of interrupting them with the modal.
  useEffect(() => {
    if (!isAuthenticated) return;
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'pointerdown',
      'wheel',
      'touchstart',
    ];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActive));
    };
  }, [isAuthenticated]);

  if (!visible) return null;

  return (
    <SessionExpiryModal
      secondsLeft={secondsLeft}
      renewing={renewing}
      onRenew={handleRenew}
      onLogout={handleExpired}
    />
  );
}

export default SessionExpiryManager;
